import React, { useState, useEffect, useCallback } from 'react';
import { getCategorias, deleteCategoria, addCategoria, getCategoriasFlat, reactivateCategoria, Categoria } from '../services/api';
import { getHierarchicalCategories } from '../utils/categoryUtils';
import CategoriaEditModal from '../components/CategoriaEditModal';
import {
    Container,
    Typography,
    CircularProgress,
    Box,
    IconButton,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Tooltip,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Autocomplete,
    Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import RestoreIcon from '@mui/icons-material/Restore';

const CategoriasPage: React.FC = () => {
    const [categorias, setCategorias] = useState<Categoria[]>([]);
    const [flatCategorias, setFlatCategorias] = useState<Categoria[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [categoriaToEdit, setCategoriaToEdit] = useState<Categoria | null>(null);

    // Add Modal State
    const [isAddModalOpen, setAddModalOpen] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newCategoryParentId, setNewCategoryParentId] = useState<number | string>('');
    const [showInactive, setShowInactive] = useState(false);

    const fetchCategorias = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [data, flatData] = await Promise.all([
                getCategorias(showInactive),
                getCategoriasFlat(showInactive)
            ]);
            setCategorias(data || []);
            setFlatCategorias(flatData || []);
        } catch (err) {
            console.error('Erro ao buscar categorias:', err);
            setError('Erro ao buscar dados das categorias.');
        } finally {
            setLoading(false);
        }
    }, [showInactive]);

    useEffect(() => {
        fetchCategorias();
    }, [fetchCategorias]);

    const handleDelete = async (categoria: Categoria, event: React.MouseEvent) => {
        event.stopPropagation();
        const activeSubs = categoria.subcategorias?.filter(s => s.ativo).length || 0;
        const msg = activeSubs > 0
            ? `Esta categoria principal possui ${activeSubs} subcategoria(s) ativa(s). Deseja desativá-la mesmo assim?`
            : 'Deseja desativar esta categoria? Ela poderá ser reativada depois.';

        if (window.confirm(msg)) {
            try {
                await deleteCategoria(categoria.id);
                fetchCategorias();
            } catch (err: any) {
                const errorMsg = err.message || 'Erro ao desativar categoria.';
                setError(errorMsg);
            }
        }
    };

    const handleReactivate = async (id: number, event: React.MouseEvent) => {
        event.stopPropagation();
        try {
            await reactivateCategoria(id);
            fetchCategorias();
        } catch (err: any) {
            setError(err.message || 'Erro ao reativar categoria.');
        }
    };

    const handleEditClick = (categoria: Categoria, event: React.MouseEvent) => {
        event.stopPropagation();
        setCategoriaToEdit(categoria);
        setEditModalOpen(true);
    };

    const handleAddClick = (parentId: number | string = '', event?: React.MouseEvent) => {
        if (event) event.stopPropagation();
        setNewCategoryName('');
        setNewCategoryParentId(parentId);
        setAddModalOpen(true);
    };

    const handleSaveNewCategory = async () => {
        if (!newCategoryName.trim()) {
            alert('O nome da categoria é obrigatório.');
            return;
        }

        try {
            await addCategoria({
                nome: newCategoryName.trim(),
                categoria_pai_id: newCategoryParentId ? Number(newCategoryParentId) : null
            });
            setAddModalOpen(false);
            fetchCategorias();
        } catch (err: any) {
            console.error('Erro ao adicionar categoria:', err);
            alert(err.message || 'Erro ao adicionar categoria.');
        }
    };

    const renderCategorias = (categoriasList: Categoria[]) => {
        return categoriasList.map(categoria => {
            const subCount = categoria.subcategorias?.length || 0;
            const hasSubcategories = subCount > 0;

            return (
                <Accordion key={categoria.id} disableGutters elevation={1} sx={{ mb: 1, '&:before': { display: 'none' }, opacity: categoria.ativo ? 1 : 0.5 }}>
                    <AccordionSummary
                        expandIcon={hasSubcategories ? <ExpandMoreIcon /> : null}
                        aria-controls={`panel-${categoria.id}-content`}
                        id={`panel-${categoria.id}-header`}
                        sx={{
                            flexDirection: 'row-reverse',
                            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                                transform: 'rotate(90deg)',
                            },
                            '& .MuiAccordionSummary-content': {
                                marginLeft: 2,
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {hasSubcategories ? <FolderIcon color="primary" sx={{ mr: 1 }} /> : <FolderOpenIcon color="action" sx={{ mr: 1 }} />}
                            <Typography variant="subtitle1" fontWeight="medium">{categoria.nome}</Typography>
                            <Chip
                                label={`${subCount} subcategoria${subCount === 1 ? '' : 's'}`}
                                size="small"
                                variant="outlined"
                                color={subCount > 0 ? 'primary' : 'warning'}
                            />
                            {!categoria.ativo && <Chip label="Inativo" size="small" color="default" />}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {categoria.ativo ? (
                                <>
                                    <Tooltip title="Adicionar Subcategoria">
                                        <IconButton size="small" color="primary" onClick={(e) => handleAddClick(categoria.id, e)} component="div" role="button" tabIndex={0}>
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Editar">
                                        <IconButton size="small" onClick={(e) => handleEditClick(categoria, e)} component="div" role="button" tabIndex={0}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Desativar">
                                        <IconButton size="small" color="error" onClick={(e) => handleDelete(categoria, e)} component="div" role="button" tabIndex={0}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            ) : (
                                <Tooltip title="Reativar">
                                    <IconButton size="small" color="success" onClick={(e) => handleReactivate(categoria.id, e)} component="div" role="button" tabIndex={0}>
                                        <RestoreIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </AccordionSummary>
                    {hasSubcategories && (
                        <AccordionDetails sx={{ bgcolor: 'background.default', p: 0 }}>
                            <List disablePadding>
                                {categoria.subcategorias!.map(sub => (
                                    <ListItem key={sub.id} divider sx={{ pl: 6, opacity: sub.ativo ? 1 : 0.5 }}>
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <SubdirectoryArrowRightIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {sub.nome}
                                                {!sub.ativo && <Chip label="Inativo" size="small" color="default" />}
                                            </Box>
                                        } />
                                        <Box>
                                            {sub.ativo ? (
                                                <>
                                                    <Tooltip title="Editar Subcategoria">
                                                        <IconButton size="small" onClick={(e) => handleEditClick(sub, e)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Desativar Subcategoria">
                                                        <IconButton size="small" color="error" onClick={(e) => handleDelete(sub, e)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            ) : (
                                                <Tooltip title="Reativar Subcategoria">
                                                    <IconButton size="small" color="success" onClick={(e) => handleReactivate(sub.id, e)}>
                                                        <RestoreIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </ListItem>
                                ))}
                            </List>
                        </AccordionDetails>
                    )}
                </Accordion>
            );
        });
    };

    return (
        <Container maxWidth="md">
            <Box sx={{ my: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" component="h1" color="primary.main" sx={{ fontWeight: 'bold' }}>
                        Categorias de Despesa
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Categorias principais agrupam o orçamento; itens e lançamentos são vinculados às subcategorias.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant={showInactive ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setShowInactive(!showInactive)}
                        color="inherit"
                    >
                        {showInactive ? 'Ocultar Inativos' : 'Mostrar Inativos'}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddClick('')}
                        sx={{ borderRadius: 2 }}
                    >
                        Nova Categoria
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
                    <CircularProgress />
                </Box>
            ) : categorias.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">Nenhuma categoria encontrada.</Typography>
                    <Button variant="outlined" sx={{ mt: 2 }} onClick={() => handleAddClick('')}>
                        Adicionar a primeira categoria
                    </Button>
                </Paper>
            ) : (
                <Box>
                    {renderCategorias(categorias)}
                </Box>
            )}

            {/* Add Category Modal */}
            <Dialog open={isAddModalOpen} onClose={() => setAddModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Adicionar Nova Categoria</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Autocomplete
                            freeSolo
                            options={flatCategorias.map((option) => option.nome)}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    {option} <span style={{ color: 'gray', fontSize: '0.8em', marginLeft: '8px' }}>(Cadastrado)</span>
                                </li>
                            )}
                            value={newCategoryName}
                            onInputChange={(event, newInputValue) => {
                                setNewCategoryName(newInputValue);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    autoFocus
                                    label="Nome da Categoria / Subcategoria"
                                    fullWidth
                                    required
                                />
                            )}
                        />
                        <TextField
                            select
                            label="Categoria Pai (Opcional)"
                            fullWidth
                            value={newCategoryParentId}
                            onChange={(e) => setNewCategoryParentId(e.target.value)}
                            helperText="Deixe em branco para criar uma Categoria Principal ou selecione uma existente para criar uma Subcategoria."
                        >
                            <MenuItem value="">
                                <em>Nenhuma (Categoria Principal)</em>
                            </MenuItem>
                            {getHierarchicalCategories(flatCategorias).map((cat) => (
                                <MenuItem
                                    key={cat.id}
                                    value={cat.id}
                                    disabled={cat.level > 0}
                                    sx={{ pl: (cat.level * 2) + 2 }}
                                >
                                    {cat.level > 0 ? '— '.repeat(cat.level) : ''}{cat.nome}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddModalOpen(false)}>Cancelar</Button>
                    <Button onClick={handleSaveNewCategory} variant="contained">Adicionar</Button>
                </DialogActions>
            </Dialog>

            <CategoriaEditModal
                categoria={categoriaToEdit}
                open={isEditModalOpen}
                onClose={() => setEditModalOpen(false)}
                onCategoriaUpdated={fetchCategorias}
            />
        </Container>
    );
};

export default CategoriasPage;
